import React, { useCallback, useMemo, useEffect, useState } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Node,
  Edge,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { AutomationNodeType } from '@frontend/app/types/globalTypes';
import { AutomationTriggerV2, AutomationNodeV2, FlowNodeData, NodeTemplate, AutomationEditorActions } from '../types';
import { generateNodeId } from '../utils';
import { TriggerNodeV2, DelayNodeV2, ConditionNodeV2, ActionNodeV2, BranchNodeV2 } from '../NodeComponents';
import { NodeOptionBar } from '../NodeOptionBar';

// Node types for ReactFlow - use V2 components
const nodeTypes = {
  TRIGGER: TriggerNodeV2,
  [AutomationNodeType.DELAY]: DelayNodeV2,
  [AutomationNodeType.CONDITION]: ConditionNodeV2,
  [AutomationNodeType.ACTION]: ActionNodeV2,
  BRANCH: BranchNodeV2,
};

interface FlowCanvasProps {
  trigger: AutomationTriggerV2;
  isEditing: boolean;
  selectedNodeId: string | null;
  onNodeClick: (nodeId: string) => void;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  onAddChild: (parentId: string, template: NodeTemplate) => void;
  onDeleteNode: (nodeId: string) => void;
  actions: AutomationEditorActions;
  automationId?: string;
  templateId?: string;
  actionOptions?: any[];
}

export const FlowCanvas: React.FC<FlowCanvasProps> = ({
  trigger,
  isEditing,
  selectedNodeId,
  onNodeClick,
  onUpdateNode,
  onAddChild,
  onDeleteNode,
  actions,
  automationId,
  templateId,
  actionOptions,
}) => {
  const { fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [showOptionBar, setShowOptionBar] = useState<{ nodeId: string; position: { x: number; y: number }; isElseBranch?: boolean } | null>(null);

  // Memoize callback functions to prevent infinite loops
  const memoizedOnNodeClick = useCallback(onNodeClick, [onNodeClick]);
  const memoizedOnUpdateNode = useCallback(onUpdateNode, [onUpdateNode]);
  const memoizedOnAddChild = useCallback(onAddChild, [onAddChild]);
  const memoizedOnDeleteNode = useCallback(onDeleteNode, [onDeleteNode]);

  // Convert automation tree to ReactFlow nodes and edges using V2 system
  const { flowNodes, flowEdges } = useMemo(() => {
    if (!trigger || !trigger.root) {
      return { flowNodes: [], flowEdges: [] };
    }
    const flowNodes: Node<FlowNodeData>[] = [];
    const flowEdges: Edge[] = [];

    // Layout configuration
    const NODE_WIDTH = 280;
    const HORIZONTAL_SPACING = 350; // Reduced to prevent overlapping
    const BASE_VERTICAL_SPACING = 200; // Increased base spacing between nodes
    const CONDITION_BASE_HEIGHT = 20; // Base height for condition nodes
    const CONDITION_ITEM_HEIGHT = 32; // Height per condition item
    const CONDITION_PADDING = 24; // Padding for condition items
    const ADD_BUTTON_HEIGHT = 32; // Height for "Add Condition" button
    const DESCRIPTION_HEIGHT = 40; // Height for description text
    const TRIGGER_HEIGHT = 120; // Height for trigger nodes
    const OTHER_NODE_HEIGHT = 100; // Increased height for other node types (delay, action, etc.)

    // Dynamic spacing calculation based on actual node content height
    const getVerticalSpacing = (node: AutomationNodeV2): number => {
      if (node.type === 'CONDITION') {
        const conditions = node.metadata?.children || [];
        const singleCondition = node.metadata?.condition;
        const hasConditions = conditions.length > 0 || singleCondition;

        // Calculate actual content height
        let contentHeight = CONDITION_BASE_HEIGHT; // Base card height

        if (hasConditions) {
          // Add height for each condition badge
          const totalConditions = conditions.length + (singleCondition && conditions.length === 0 ? 1 : 0);
          contentHeight += totalConditions * CONDITION_ITEM_HEIGHT;
          contentHeight += (totalConditions - 1) * CONDITION_PADDING; // Spacing between conditions
        } else {
          // No conditions - add height for "No conditions set" badge
          contentHeight += CONDITION_ITEM_HEIGHT;
        }

        // Add height for "Add Condition" button if editing
        if (isEditing) {
          contentHeight += ADD_BUTTON_HEIGHT + CONDITION_PADDING;
        }

        // Add height for description text
        contentHeight += DESCRIPTION_HEIGHT;

        // Add height for True/False buttons at bottom
        if (isEditing) {
          contentHeight += 60; // Space for True/False buttons
        }

        // Calculate spacing needed to prevent overlap with extra padding for condition nodes
        const spacingNeeded = contentHeight + BASE_VERTICAL_SPACING + 50; // Extra padding for condition nodes

        return spacingNeeded;
      }

      // For non-condition nodes, use base spacing plus node height
      return BASE_VERTICAL_SPACING + OTHER_NODE_HEIGHT;
    };

    // Calculate positions using a hierarchical layout
    const calculateLayout = (node: AutomationNodeV2, x: number, y: number, depth: number): void => {
      // Add extra spacing for the first few nodes to prevent overlap
      const extraSpacing = depth <= 3 ? 80 : 30; // Increased spacing for first 3 levels
      // Add the current node
      flowNodes.push({
        id: node.id,
        type: node.type,
        position: { x, y },
        data: {
          nodeId: node.id,
          nodeType: node.type,
          subType: node.subType,
          metadata: node.metadata,
          isEditing,
          isSelected: node.id === selectedNodeId,
          onNodeClick: memoizedOnNodeClick,
          onUpdateNode: memoizedOnUpdateNode,
          onAddChild: memoizedOnAddChild,
          onDeleteNode: memoizedOnDeleteNode,
          onShowOptionBar: (nodeId: string, position: { x: number; y: number }, isElseBranch?: boolean) => {
            setShowOptionBar({ nodeId, position, isElseBranch });
          },
          automationId,
          templateId,
          actionOptions,
        },
        draggable: false,
        selectable: true,
      });

      // Handle children and elseChildren for condition nodes
      if (node.type === 'CONDITION') {
        const trueChildren = node.children || [];
        const falseChildren = node.elseChildren || [];

        // If we have children, treat them as True branch
        if (trueChildren.length > 0) {
          const childY = y + getVerticalSpacing(node) + extraSpacing;

          trueChildren.forEach((child, index) => {
            // For single child, place directly below, for multiple spread horizontally
            const childX = trueChildren.length === 1 ? x : x + (index - (trueChildren.length - 1) / 2) * HORIZONTAL_SPACING;
            calculateLayout(child, childX, childY, depth + 1);

            flowEdges.push({
              id: `${node.id}-true-${child.id}`,
              source: node.id,
              target: child.id,
              sourceHandle: 'true',
              type: 'smoothstep',
              label: 'True',
              labelStyle: {
                fontSize: 12,
                fontWeight: 600,
                fill: '#000000',
              },
              labelBgStyle: {
                fill: '#f0fdf4',
                fillOpacity: 0.95,
                borderRadius: '6px',
                stroke: '#000000',
                strokeWidth: 1,
              },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#000000',
                width: 20,
                height: 20,
              },
              style: {
                stroke: '#000000',
                strokeWidth: 3,
                strokeDasharray: '0',
              },
            });
          });
        }

        // Process false branch if exists
        if (falseChildren.length > 0) {
          const childY = y + getVerticalSpacing(node) + extraSpacing;

          falseChildren.forEach((child, index) => {
            const childX = falseChildren.length === 1 ? x + HORIZONTAL_SPACING : x + HORIZONTAL_SPACING + (index * HORIZONTAL_SPACING);
            calculateLayout(child, childX, childY, depth + 1);

            flowEdges.push({
              id: `${node.id}-false-${child.id}`,
              source: node.id,
              target: child.id,
              sourceHandle: 'false',
              type: 'smoothstep',
              label: 'False',
              labelStyle: {
                fontSize: 12,
                fontWeight: 600,
                fill: '#000000',
              },
              labelBgStyle: {
                fill: '#fef2f2',
                fillOpacity: 0.95,
                borderRadius: '6px',
                stroke: '#000000',
                strokeWidth: 1,
              },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#000000',
                width: 20,
                height: 20,
              },
              style: {
                stroke: '#000000',
                strokeWidth: 3,
                strokeDasharray: '0',
              },
            });
          });
        }
      } else if (node.children && node.children.length > 0) {
        if (node.children.length === 1) {
          // Single child - place directly below
          const childX = x;
          const childY = y + getVerticalSpacing(node) + extraSpacing;
          calculateLayout(node.children[0], childX, childY, depth + 1);

          // Add edge
          flowEdges.push({
            id: `${node.id}-${node.children[0].id}`,
            source: node.id,
            target: node.children[0].id,
            type: 'smoothstep',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#000000',
              width: 18,
              height: 18,
            },
            style: {
              stroke: '#000000',
              strokeWidth: 2.5,
              strokeDasharray: '0',
            },
          });
        } else {
          // Multiple children - spread horizontally (branching)
          const totalWidth = (node.children.length - 1) * HORIZONTAL_SPACING;
          const startX = x - totalWidth / 2;
          const childY = y + getVerticalSpacing(node) + extraSpacing;

          node.children.forEach((child, index) => {
            const childX = startX + index * HORIZONTAL_SPACING;
            calculateLayout(child, childX, childY, depth + 1);

            // Add edge with branch labels for conditions
            const edgeLabel = node.type === 'CONDITION'
              ? (index === 0 ? 'True' : 'False')
              : undefined;

            flowEdges.push({
              id: `${node.id}-${child.id}`,
              source: node.id,
              target: child.id,
              type: 'smoothstep',
              label: edgeLabel,
              labelStyle: {
                fontSize: 12,
                fontWeight: 500,
                fill: '#1f2937',
              },
              labelBgStyle: {
                fill: '#fff',
                fillOpacity: 0.9,
                borderRadius: '4px',
              },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#000000',
              },
              style: {
                stroke: '#000000',
                strokeWidth: 2,
              },
            });
          });
        }
      }
    };

        // Start with trigger node - center it horizontally
    flowNodes.push({
      id: trigger.id,
      type: 'TRIGGER', // Use TRIGGER type for proper node rendering
      position: { x: -NODE_WIDTH / 2, y: 0 },
      data: {
        nodeId: trigger.id,
        nodeType: 'TRIGGER' as any,
        metadata: { triggerType: trigger.type }, // Pass trigger type in correct format
        isEditing,
        isSelected: trigger.id === selectedNodeId,
        onNodeClick: memoizedOnNodeClick,
        onUpdateNode: memoizedOnUpdateNode,
        onAddChild: memoizedOnAddChild,
        onDeleteNode: memoizedOnDeleteNode,
        onShowOptionBar: (nodeId: string, position: { x: number; y: number }, isElseBranch?: boolean) => {
          console.log('🎯 FlowCanvas onShowOptionBar (trigger):', { nodeId, position, isElseBranch });
          setShowOptionBar({ nodeId, position, isElseBranch });
        },
      },
      draggable: false,
      selectable: true,
    });

    // Add edge from trigger to root node
    if (trigger.root) {
      flowEdges.push({
        id: `trigger-${trigger.root.id}`,
        source: trigger.id,
        target: trigger.root.id,
        type: 'smoothstep',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#000000',
          width: 18,
          height: 18,
        },
        style: {
          stroke: '#000000',
          strokeWidth: 2.5,
          strokeDasharray: '0',
        },
      });

      // Layout the automation tree starting from root - center it horizontally
      // Use trigger height + base spacing for proper positioning
      calculateLayout(trigger.root, -NODE_WIDTH / 2, TRIGGER_HEIGHT + BASE_VERTICAL_SPACING, 1);
    }

    return { flowNodes, flowEdges };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, isEditing, selectedNodeId]);

  // Update nodes and edges when trigger changes
  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  // Fit view when nodes change with better padding for dynamic content
  useEffect(() => {
    if (flowNodes.length > 0) {
      setTimeout(() => {
        fitView({
          padding: 0.15, // Adjusted padding for better view
          includeHiddenNodes: false,
          duration: 800, // Smooth animation
        });
      }, 150); // Slightly longer delay to ensure all nodes are rendered
    }
  }, [flowNodes.length, fitView]);

  // Handle node clicks
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.stopPropagation();
    onNodeClick(node.id);
  }, [onNodeClick]);

  // Handle canvas click (deselect)
  const handlePaneClick = useCallback(() => {
    onNodeClick(null); // Always deselect when clicking on canvas
    setShowOptionBar(null);
  }, [onNodeClick]);

  // Handle drag over
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  // Handle drop
  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    try {
      const templateData = event.dataTransfer.getData('application/json');
      const template: NodeTemplate = JSON.parse(templateData);

      // For now, add to the root node - you could enhance this to detect the target node
      const newNode: AutomationNodeV2 = {
        id: generateNodeId(),
        type: template.type,
        subType: template.subType,
        children: [],
        metadata: { ...template.defaultMetadata },
        variables: [],
      };

      // Add to the first available parent (root in this case)
      actions.addNode(trigger.root.id, newNode);
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  }, [actions, trigger.root?.id]);

  return (
    <div
      className="h-full w-full relative"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={isEditing}
        selectNodesOnDrag={false}
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        className="bg-grey-1"
        fitView
        minZoom={0.3}
        maxZoom={1.8}
        defaultEdgeOptions={{
          type: 'smoothstep',
          style: { strokeWidth: 2.5 },
        }}
        connectionLineStyle={{ strokeWidth: 2.5 }}
      >
        <Background
          color="#e5e8eb"
          gap={16}
          size={1}
          style={{ opacity: isEditing ? 0.5 : 0.2 }}
        />
        <Controls
          showInteractive={false}
          className="bg-white border border-grey-2 rounded-lg shadow-sm"
        />
      </ReactFlow>

      {/* Node Option Bar */}
      {showOptionBar && (
        <NodeOptionBar
          parentNodeId={showOptionBar.nodeId}
          onAddNode={onAddChild}
          onClose={() => setShowOptionBar(null)}
          position={showOptionBar.position}
          isElseBranch={showOptionBar.isElseBranch}
        />
      )}
    </div>
  );
};
